/**
 * LA RENTABILITÉ — par camion et par trajet (lot 102, 18/09/2026).
 *
 * Cadrage « Analyse financière : 6. la rentabilité » et « 8. les arbitrages »,
 * maquettes `maquette-analyse-rentabilite-v2.html` et `-v3.html`. Pur.
 *
 * LES MONTANTS SONT EN CENTIMES. Un COÛT est POSITIF ici (c'est une dépense),
 * contrairement aux feuilles du SIG, qui sont signées.
 *
 * CE QUE CET ÉCRAN DIT, ET QUE LE RÉSULTAT NE DIT PAS : avec QUOI on gagne ou
 * on perd — par camion, par trajet, au kilomètre et au jour (doc 6).
 *
 * LES CLÉS DE RÉPARTITION DU RÉEL, et d'où elles viennent :
 *   - carburant, péages, entretien : au prorata des kilomètres PONDÉRÉS PAR LE
 *     COÛT AU KILOMÈTRE PRÉVU du véhicule (doc 8, tranché le 17/09) — un
 *     tracteur ne consomme pas comme un véhicule léger ;
 *   - loyers : au prorata du LOYER PRÉVU de chaque véhicule (prix de revient).
 *     Le cadrage disait « loyers exacts, une facture par contrat » : la
 *     comptabilité lue ne rattache aucune facture à un véhicule, donc la clé
 *     est le loyer prévu, et l'écran le dit ;
 *   - salaires : COÛT EMPLOYEUR SAISI par chauffeur et par mois, rattaché aux
 *     camions au prorata des JOURS D'ATTELAGE (doc 8, 17/09). Non saisi : le
 *     salaire prévu du prix de revient, marqué « estimé » ;
 *   - structure : à PARTS ÉGALES entre les véhicules (doc 6).
 *
 * LE POINT MORT est celui de la FLOTTE SEULE : la marge d'affrètement n'est pas
 * déduite (doc 8). Fixes ÷ taux de marge sur coûts variables.
 *
 * LA LIMITE, dite franchement (doc 6) : une répartition au prorata efface les
 * différences réelles de consommation entre camions. Pour aller plus loin, il
 * faudrait relier chaque carte carburant et chaque badge à un camion.
 */

export interface VehiculeRenta {
  readonly id: string;
  readonly immatriculation: string;
  readonly type: string | null;
  /** Journées roulées du mois. */
  readonly jours: number;
  readonly km: number;
  readonly km_vide: number;
  /** Chiffre d'affaires tombé sur ses journées, en centimes. */
  readonly ca: number;
  /** Coût de revient PRÉVU (prix de revient Stafa) de ses journées. */
  readonly cout_prevu: number;
  /**
   * La part VARIABLE du prévu — gazole et frais au kilomètre. C'est
   * exactement « les kilomètres pondérés par le coût au km prévu » (doc 8) :
   * km × (coût variable au km), et c'est la clé du carburant, des péages et
   * de l'entretien. Un camion qui n'a pas roulé pèse zéro.
   */
  readonly variable_prevu: number;
  /** Part « matériel » du prévu (tracteur + remorque) : la clé des loyers. */
  readonly loyer_prevu: number;
}

export type PosteReel =
  'carburant' | 'peages' | 'entretien' | 'loyers' | 'salaires' | 'structure';

export const POSTES_REELS: readonly {
  readonly cle: PosteReel;
  readonly nom: string;
  readonly variable: boolean;
  readonly cledit: string;
}[] = [
  {
    cle: 'carburant',
    nom: 'Carburant',
    variable: true,
    cledit: 'prorata des km pondérés par le coût au km prévu',
  },
  {
    cle: 'peages',
    nom: 'Péages',
    variable: true,
    cledit: 'prorata des km pondérés par le coût au km prévu',
  },
  {
    cle: 'entretien',
    nom: 'Entretien, pneus',
    variable: true,
    cledit: 'prorata des km pondérés par le coût au km prévu',
  },
  {
    cle: 'loyers',
    nom: 'Loyers des véhicules',
    variable: false,
    cledit: 'prorata du loyer prévu',
  },
  {
    cle: 'salaires',
    nom: 'Salaires et charges',
    variable: false,
    cledit: 'jours d’attelage du conducteur',
  },
  { cle: 'structure', nom: 'Structure', variable: false, cledit: 'parts égales' },
];

/** Le réel du mois, par poste, en centimes POSITIFS. */
export type ReelDuMois = Readonly<Record<PosteReel, number>>;

/** Les jours d'attelage d'un conducteur sur un véhicule, dans le mois. */
export interface Attelage {
  readonly chauffeur_id: string;
  readonly chauffeur_nom: string;
  readonly vehicule_id: string;
  readonly jours: number;
}

/** Ce que coûte un conducteur ce mois-là. */
export interface SalaireChauffeur {
  readonly chauffeur_id: string;
  readonly nom: string;
  /** Coût employeur SAISI, ou nul. */
  readonly saisi: number | null;
  /** Salaire PRÉVU du prix de revient, replié quand rien n'est saisi. */
  readonly prevu: number;
}

const somme = <T>(l: readonly T[], f: (x: T) => number) =>
  l.reduce((s, x) => s + f(x), 0);

/**
 * RÉPARTIT UN TOTAL au prorata des poids, en centimes entiers. Le reliquat va
 * à la plus grosse part, comme la ventilation de TVA : sans cela, la somme des
 * parts ne ferait plus le total et l'écart grandirait à chaque mois.
 *
 * Tous les poids à zéro : parts égales — le total doit bien tomber quelque
 * part, et n'en retenir aucune le ferait disparaître.
 */
export function repartir(
  poids: readonly { readonly cle: string; readonly poids: number }[],
  total: number,
): Map<string, number> {
  const res = new Map<string, number>();
  if (poids.length === 0) return res;
  const t = somme(poids, (p) => Math.max(0, p.poids));
  const parts = poids.map((p) =>
    t <= 0
      ? Math.floor(total / poids.length)
      : Math.floor((total * Math.max(0, p.poids)) / t),
  );
  const reste = total - parts.reduce((a, b) => a + b, 0);
  let plusGrosse = 0;
  parts.forEach((v, i) => {
    if (v > (parts[plusGrosse] ?? 0)) plusGrosse = i;
  });
  parts[plusGrosse] = (parts[plusGrosse] ?? 0) + reste;
  poids.forEach((p, i) => res.set(p.cle, parts[i] ?? 0));
  return res;
}

export interface SalairesRepartis {
  /** Par véhicule. */
  readonly parVehicule: ReadonlyMap<string, number>;
  /** Par conducteur : ce qu'il coûte, et si c'est une estimation. */
  readonly parChauffeur: readonly {
    readonly chauffeur_id: string;
    readonly nom: string;
    readonly cout: number;
    readonly estime: boolean;
    readonly jours: number;
  }[];
  /** Le total réparti. */
  readonly total: number;
  /** Vrai dès qu'un conducteur est estimé. */
  readonly estime: boolean;
}

/**
 * LE SALAIRE DE CHAQUE CONDUCTEUR, rattaché aux camions qu'il a attelés, au
 * prorata de ses jours d'attelage. Un conducteur sans aucun jour d'attelage ne
 * se rattache à aucun camion : son coût reste hors des lignes, et le total le
 * dit.
 */
export function repartirSalaires(
  salaires: readonly SalaireChauffeur[],
  attelages: readonly Attelage[],
): SalairesRepartis {
  const parVehicule = new Map<string, number>();
  const parChauffeur: SalairesRepartis['parChauffeur'][number][] = [];
  let total = 0;
  let estime = false;
  for (const s of salaires) {
    const siens = attelages.filter((a) => a.chauffeur_id === s.chauffeur_id);
    const jours = somme(siens, (a) => a.jours);
    const cout = s.saisi ?? s.prevu;
    parChauffeur.push({
      chauffeur_id: s.chauffeur_id,
      nom: s.nom,
      cout,
      estime: s.saisi === null,
      jours,
    });
    if (s.saisi === null) estime = true;
    if (jours <= 0 || cout === 0) continue;
    total += cout;
    const parts = repartir(
      siens.map((a) => ({ cle: a.vehicule_id, poids: a.jours })),
      cout,
    );
    for (const [v, m] of parts) parVehicule.set(v, (parVehicule.get(v) ?? 0) + m);
  }
  return { parVehicule, parChauffeur, total, estime };
}

export interface LigneRenta {
  readonly vehicule: VehiculeRenta;
  readonly part_vide: number | null;
  readonly ca_km: number | null;
  readonly ca_jour: number | null;
  /** Le réel réparti, poste par poste. */
  readonly postes: Readonly<Record<PosteReel, number>>;
  readonly cout_reel: number;
  readonly ecart: number;
  readonly marge: number;
  readonly taux: number | null;
  readonly marge_km: number | null;
  readonly marge_jour: number | null;
}

export interface RentabiliteDuMois {
  readonly lignes: readonly LigneRenta[];
  readonly ca: number;
  readonly cout_prevu: number;
  readonly cout_reel: number;
  readonly marge: number;
  readonly km: number;
  readonly km_vide: number;
  readonly salaires: SalairesRepartis;
}

export function rentabiliteDuMois(
  vehicules: readonly VehiculeRenta[],
  reel: ReelDuMois,
  salaires: readonly SalaireChauffeur[],
  attelages: readonly Attelage[],
): RentabiliteDuMois {
  const repartis = repartirSalaires(salaires, attelages);
  // Les kilomètres pondérés par le coût variable au km prévu du véhicule.
  const poidsKm = vehicules.map((v) => ({
    cle: v.id,
    poids: v.km > 0 ? (v.variable_prevu > 0 ? v.variable_prevu : v.km) : 0,
  }));
  const parPoste = {
    carburant: repartir(poidsKm, reel.carburant),
    peages: repartir(poidsKm, reel.peages),
    entretien: repartir(poidsKm, reel.entretien),
    loyers: repartir(
      vehicules.map((v) => ({ cle: v.id, poids: v.loyer_prevu })),
      reel.loyers,
    ),
    structure: repartir(
      vehicules.map((v) => ({ cle: v.id, poids: 1 })),
      reel.structure,
    ),
  };
  const lignes = vehicules.map((v) => {
    const postes: Record<PosteReel, number> = {
      carburant: parPoste.carburant.get(v.id) ?? 0,
      peages: parPoste.peages.get(v.id) ?? 0,
      entretien: parPoste.entretien.get(v.id) ?? 0,
      loyers: parPoste.loyers.get(v.id) ?? 0,
      salaires: repartis.parVehicule.get(v.id) ?? 0,
      structure: parPoste.structure.get(v.id) ?? 0,
    };
    const coutReel = POSTES_REELS.reduce((s, p) => s + postes[p.cle], 0);
    const marge = v.ca - coutReel;
    return {
      vehicule: v,
      part_vide: v.km > 0 ? v.km_vide / v.km : null,
      ca_km: v.km > 0 ? Math.round(v.ca / v.km) : null,
      ca_jour: v.jours > 0 ? Math.round(v.ca / v.jours) : null,
      postes,
      cout_reel: coutReel,
      ecart: coutReel - v.cout_prevu,
      marge,
      taux: v.ca > 0 ? marge / v.ca : null,
      marge_km: v.km > 0 ? Math.round(marge / v.km) : null,
      marge_jour: v.jours > 0 ? Math.round(marge / v.jours) : null,
    };
  });
  return {
    lignes,
    ca: somme(vehicules, (v) => v.ca),
    cout_prevu: somme(vehicules, (v) => v.cout_prevu),
    cout_reel: somme(lignes, (l) => l.cout_reel),
    marge: somme(lignes, (l) => l.marge),
    km: somme(vehicules, (v) => v.km),
    km_vide: somme(vehicules, (v) => v.km_vide),
    salaires: repartis,
  };
}

// ---------------------------------------------------------------------------
// Le point mort
// ---------------------------------------------------------------------------

export interface PointMort {
  readonly fixes: number;
  readonly variables: number;
  /** Marge sur coûts variables ÷ CA. Nul sans chiffre d'affaires. */
  readonly taux: number | null;
  /** Le chiffre d'affaires qu'il faut faire. Nul quand le taux l'est. */
  readonly seuil: number | null;
  /** Le jour où le CA cumulé passe le seuil, ou nul. */
  readonly atteint_le: string | null;
  /** CA cumulé jour par jour. */
  readonly cumul: readonly { readonly jour: string; readonly ca: number }[];
  /** Projection du mois entier, au rythme des jours écoulés (mois en cours). */
  readonly projection: number | null;
}

export function pointMort(
  reel: ReelDuMois,
  ca: number,
  parJour: readonly { readonly jour: string; readonly ca: number }[],
  joursOuvres: { readonly ecoules: number; readonly total: number } | null,
): PointMort {
  const variables = reel.carburant + reel.peages + reel.entretien;
  const fixes = reel.loyers + reel.salaires + reel.structure;
  const taux = ca > 0 ? (ca - variables) / ca : null;
  const seuil = taux === null || taux <= 0 ? null : Math.round(fixes / taux);
  const cumul: { jour: string; ca: number }[] = [];
  let courant = 0;
  for (const j of [...parJour].sort((a, b) => (a.jour < b.jour ? -1 : 1))) {
    courant += j.ca;
    cumul.push({ jour: j.jour, ca: courant });
  }
  return {
    fixes,
    variables,
    taux,
    seuil,
    atteint_le:
      seuil === null ? null : (cumul.find((c) => c.ca >= seuil)?.jour ?? null),
    cumul,
    projection:
      joursOuvres === null || joursOuvres.ecoules <= 0
        ? null
        : Math.round((ca * joursOuvres.total) / joursOuvres.ecoules),
  };
}

// ---------------------------------------------------------------------------
// Les trajets
// ---------------------------------------------------------------------------

/**
 * LA MAILLE D'UN TRAJET. Le cadrage en demandait trois — département, ville,
 * RÉGION. La région n'est pas livrée : Stafa ne sait pas dans quelle région
 * est un site (aucune table, aucune colonne — vérifié dans
 * `20260903190000_annuaire_sites.sql` et `20260903160000_vehicules_et_zones.sql`,
 * qui n'a que les départements). L'inventer d'après le code postal
 * demanderait une table de correspondance que personne n'a validée. L'écran
 * le dit.
 */
export type Maille = 'departement' | 'ville';

export interface TransportRenta {
  readonly ot_id: string;
  readonly numero: string;
  readonly jour: string;
  readonly vehicule_id: string;
  readonly client_nom: string;
  readonly depart: Readonly<Record<Maille, string>>;
  readonly arrivee: Readonly<Record<Maille, string>>;
  readonly km: number;
  readonly ca: number;
}

export interface LigneTrajet {
  readonly cle: string;
  readonly depart: string;
  readonly arrivee: string;
  readonly transports: readonly TransportRenta[];
  readonly km: number;
  readonly ca: number;
  readonly cout: number;
  readonly marge: number;
  readonly taux: number | null;
  /** Moins de trois transports : la moyenne ne veut pas dire grand-chose. */
  readonly peu: boolean;
}

/**
 * LES LIGNES ORIGINE → DESTINATION, à la maille choisie. Le coût d'un
 * transport est ses kilomètres × le COÛT AU KILOMÈTRE COMPLET de son camion ce
 * mois-là (doc 6, proposition 4) : les kilomètres à vide du camion sont donc
 * déjà dans ce coût au kilomètre.
 */
export function lignesDeTrajet(
  transports: readonly TransportRenta[],
  maille: Maille,
  coutKmParVehicule: ReadonlyMap<string, number>,
): LigneTrajet[] {
  const carte = new Map<string, TransportRenta[]>();
  for (const t of transports) {
    const cle = `${t.depart[maille]} → ${t.arrivee[maille]}`;
    carte.set(cle, [...(carte.get(cle) ?? []), t]);
  }
  return [...carte]
    .map(([cle, liste]) => {
      const km = somme(liste, (t) => t.km);
      const ca = somme(liste, (t) => t.ca);
      const cout = somme(liste, (t) =>
        Math.round(t.km * (coutKmParVehicule.get(t.vehicule_id) ?? 0)),
      );
      const marge = ca - cout;
      return {
        cle,
        depart: liste[0]?.depart[maille] ?? '',
        arrivee: liste[0]?.arrivee[maille] ?? '',
        transports: liste,
        km,
        ca,
        cout,
        marge,
        taux: ca > 0 ? marge / ca : null,
        peu: liste.length < 3,
      };
    })
    .sort((a, b) => b.ca - a.ca);
}

/**
 * CONTRÔLES repris des maquettes : la somme des parts d'un poste fait le
 * total, et le coût réel d'une ligne est la somme de ses postes.
 */
export function verifierRentabilite(r: RentabiliteDuMois, reel: ReelDuMois): string[] {
  const fautes: string[] = [];
  for (const p of POSTES_REELS) {
    const reparti = r.lignes.reduce((s, l) => s + l.postes[p.cle], 0);
    const attendu = p.cle === 'salaires' ? r.salaires.total : reel[p.cle];
    if (reparti !== attendu) {
      fautes.push(`${p.cle} : ${String(reparti)} réparti pour ${String(attendu)}`);
    }
  }
  for (const l of r.lignes) {
    const somme = POSTES_REELS.reduce((s, p) => s + l.postes[p.cle], 0);
    if (somme !== l.cout_reel)
      fautes.push(`${l.vehicule.immatriculation} : postes ≠ coût réel`);
    if (l.marge !== l.vehicule.ca - l.cout_reel) {
      fautes.push(`${l.vehicule.immatriculation} : marge ≠ CA − coût`);
    }
  }
  return fautes;
}
